import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { useFavourites } from '../context/FavouritesContext';
import { useRecentlyViewed } from '../hooks/useRecentlyViewed';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { PageLoader } from '../components/LoadingSpinner';
import { VerifiedBadge, VerifiedBadgeFull } from '../components/VerifiedBadge';
import { toast } from 'sonner';
import { 
  ShoppingCart, Heart, Star, ChevronLeft, Store, 
  MessageCircle, Minus, Plus, Share2 
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categoryColors = {
  crochet: 'bg-pastel-crochet/30',
  stickers: 'bg-pastel-stickers/30',
  art: 'bg-pastel-art/30',
  keyrings: 'bg-pastel-keyrings/30'
};

export default function ProductPage() {
  const { id } = useParams();
  const { user, token } = useAuth();
  const { addToCart } = useCart();
  const { isFavourite, toggleFavourite } = useFavourites();
  const { addToRecentlyViewed } = useRecentlyViewed();
  const navigate = useNavigate();

  const [product, setProduct] = useState(null);
  const [store, setStore] = useState(null);
  const [creator, setCreator] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);
  
  // Review form
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState('');
  const [submittingReview, setSubmittingReview] = useState(false);

  useEffect(() => {
    fetchProduct();
  }, [id, user]);

  // Track recently viewed when product loads
  useEffect(() => {
    if (product) {
      addToRecentlyViewed(product);
    }
  }, [product, addToRecentlyViewed]);

  const fetchProduct = async () => {
    try {
      const res = await fetch(`${API}/products/${id}`);
      if (res.ok) {
        const data = await res.json();
        setProduct(data.product);
        setStore(data.store);
        setCreator(data.creator);
        setReviews(data.reviews || []);
      } else {
        toast.error('Product not found');
        navigate('/browse');
      }
    } catch (e) {
      toast.error('Error loading product');
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = async () => {
    if (!user) {
      toast.error('Please login to add items to cart');
      navigate('/login', { state: { from: `/product/${id}` } });
      return;
    }
    try {
      await addToCart(product.id, quantity);
      toast.success(`Added ${quantity} item(s) to cart!`);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleFavorite = () => {
    if (!user) {
      toast.error('Please login to save favourites');
      return;
    }
    toggleFavourite(product.id);
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.error('Please login to leave a review');
      return;
    }
    setSubmittingReview(true);
    try {
      const res = await fetch(`${API}/reviews`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          product_id: id,
          rating,
          comment
        })
      });
      
      if (res.ok) {
        const newReview = await res.json();
        setReviews([newReview, ...reviews]);
        setComment('');
        setRating(5);
        toast.success('Review submitted!');
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to submit review');
      }
    } catch (e) {
      toast.error('Error submitting review');
    } finally {
      setSubmittingReview(false);
    }
  };

  const handleMessage = () => {
    if (!user) {
      toast.error('Please login to message the creator');
      navigate('/login', { state: { from: `/product/${id}` } });
      return;
    }
    navigate(`/messages/${creator.id}`);
  };

  const avgRating = reviews.length > 0 
    ? (reviews.reduce((acc, r) => acc + r.rating, 0) / reviews.length).toFixed(1)
    : null;

  if (loading) return <PageLoader />;
  if (!product) return null;

  return (
    <div className="min-h-screen py-8">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Back Button */}
        <Link to="/browse" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6 transition-colors">
          <ChevronLeft className="w-4 h-4" />
          Back to Browse
        </Link>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Images */}
          <div className="space-y-4">
            <div className="aspect-square rounded-2xl overflow-hidden border-2 border-primary/10 bg-muted">
              {product.images?.[selectedImage] ? (
                <img 
                  src={product.images[selectedImage]} 
                  alt={product.name}
                  className="w-full h-full object-cover"
                  data-testid="product-main-image"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                  No image available
                </div>
              )}
            </div>
            
            {product.images?.length > 1 && (
              <div className="flex gap-2 overflow-x-auto pb-2">
                {product.images.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setSelectedImage(idx)}
                    className={`w-20 h-20 rounded-xl overflow-hidden border-2 flex-shrink-0 transition-all ${
                      selectedImage === idx ? 'border-primary shadow-brutal-sm' : 'border-primary/10'
                    }`}
                  >
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Details */}
          <div className="space-y-6">
            <div>
              <span className={`inline-block px-3 py-1 rounded-full text-sm font-semibold ${categoryColors[product.category]} capitalize mb-4`}>
                {product.category}
              </span>
              <h1 className="text-3xl md:text-4xl font-heading font-extrabold" data-testid="product-title">
                {product.name}
              </h1>
              
              {avgRating && (
                <div className="flex items-center gap-2 mt-2">
                  <div className="flex items-center">
                    {[1,2,3,4,5].map(i => (
                      <Star key={i} className={`w-5 h-5 ${i <= Math.round(avgRating) ? 'text-yellow-400 fill-yellow-400' : 'text-muted'}`} />
                    ))}
                  </div>
                  <span className="font-semibold">{avgRating}</span>
                  <span className="text-muted-foreground">({reviews.length} reviews)</span>
                </div>
              )}
            </div>

            <p className="text-4xl font-heading font-extrabold" data-testid="product-price">
              ${product.price?.toFixed(2)}
            </p>

            <p className="text-muted-foreground leading-relaxed">
              {product.description}
            </p>

            {product.tags?.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {product.tags.map((tag, idx) => (
                  <span key={idx} className="px-3 py-1 rounded-full bg-muted text-sm">
                    #{tag}
                  </span>
                ))}
              </div>
            )}

            {/* Quantity */}
            <div className="flex items-center gap-4">
              <span className="font-semibold">Quantity:</span>
              <div className="flex items-center gap-2 border-2 border-primary/20 rounded-xl p-1">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="rounded-lg"
                  data-testid="quantity-decrease"
                >
                  <Minus className="w-4 h-4" />
                </Button>
                <span className="w-12 text-center font-semibold" data-testid="quantity-display">{quantity}</span>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setQuantity(quantity + 1)}
                  className="rounded-lg"
                  data-testid="quantity-increase"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              {product.stock > 0 && (
                <span className="text-sm text-muted-foreground">{product.stock} in stock</span>
              )}
            </div>

            {/* Actions */}
            <div className="flex flex-wrap gap-3">
              <Button 
                className="flex-1 btn-brutal-primary text-lg py-6"
                onClick={handleAddToCart}
                data-testid="add-to-cart-btn"
              >
                <ShoppingCart className="w-5 h-5 mr-2" />
                Add to Cart
              </Button>
              <Button
                variant="outline"
                size="icon"
                className={`btn-brutal h-14 w-14 ${isFavourite(product.id) ? 'text-chilli' : ''}`}
                onClick={handleFavorite}
                data-testid="favourite-btn"
              >
                <Heart className={`w-6 h-6 ${isFavourite(product.id) ? 'fill-current' : ''}`} />
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="btn-brutal h-14 w-14"
                onClick={() => navigator.share?.({ title: product.name, url: window.location.href })}
              >
                <Share2 className="w-6 h-6" />
              </Button>
            </div>

            {/* Store Info */}
            {store && (
              <div className="card-brutal p-4">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-xl overflow-hidden bg-pastel-keyrings/30 flex items-center justify-center flex-shrink-0">
                    {store.logo ? (
                      <img src={store.logo} alt={store.name} className="w-full h-full object-cover" />
                    ) : (
                      <Store className="w-8 h-8" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <Link to={`/store/${store.id}`} className="font-heading font-bold hover:underline flex items-center gap-1" data-testid="store-link">
                      {store.name}
                      {store.is_verified && <VerifiedBadge size="sm" />}
                    </Link>
                    <p className="text-sm text-muted-foreground truncate">by {creator?.name}</p>
                  </div>
                  <div className="flex gap-2">
                    <Link to={`/store/${store.id}`}>
                      <Button variant="outline" size="sm" className="rounded-lg">
                        View Store
                      </Button>
                    </Link>
                    <Button variant="outline" size="sm" className="rounded-lg" onClick={handleMessage}>
                      <MessageCircle className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                {store.is_verified && (
                  <div className="mt-3 pt-3 border-t border-primary/10">
                    <VerifiedBadgeFull />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Reviews Section */}
        <div className="mt-16">
          <h2 className="text-2xl font-heading font-bold mb-6">Reviews</h2>
          
          {/* Review Form */}
          {user && (
            <form onSubmit={handleSubmitReview} className="card-brutal p-6 mb-8">
              <h3 className="font-semibold mb-4">Leave a Review</h3>
              <div className="flex items-center gap-1 mb-4">
                {[1,2,3,4,5].map(i => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setRating(i)}
                    className="p-1"
                  >
                    <Star className={`w-6 h-6 transition-colors ${i <= rating ? 'text-yellow-400 fill-yellow-400' : 'text-muted'}`} />
                  </button>
                ))}
              </div>
              <Textarea
                placeholder="Write your review..."
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                className="mb-4 min-h-[100px]"
                required
                data-testid="review-comment"
              />
              <Button type="submit" disabled={submittingReview} data-testid="submit-review-btn">
                {submittingReview ? 'Submitting...' : 'Submit Review'}
              </Button>
            </form>
          )}

          {/* Reviews List */}
          {reviews.length > 0 ? (
            <div className="space-y-4">
              {reviews.map((review) => (
                <div key={review.id} className="card-brutal p-6">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className="w-10 h-10 rounded-full bg-pastel-art/30 flex items-center justify-center font-bold">
                        {review.user_name?.[0]?.toUpperCase()}
                      </div>
                      <div>
                        <p className="font-semibold">{review.user_name}</p>
                        <div className="flex items-center">
                          {[1,2,3,4,5].map(i => (
                            <Star key={i} className={`w-4 h-4 ${i <= review.rating ? 'text-yellow-400 fill-yellow-400' : 'text-muted'}`} />
                          ))}
                        </div>
                      </div>
                    </div>
                    <span className="text-sm text-muted-foreground">
                      {new Date(review.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  <p className="text-muted-foreground">{review.comment}</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-muted-foreground py-8">No reviews yet. Be the first to review!</p>
          )}
        </div>
      </div>
    </div>
  );
}
